import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import axios from 'axios';

const AdminHostelManagement = () => {
    const { currentUser } = useSelector(state => state.user);
    const [hostels, setHostels] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [students, setStudents] = useState([]);

    const [openHostelDialog, setOpenHostelDialog] = useState(false);
    const [openRoomDialog, setOpenRoomDialog] = useState(false);
    const [openAllocDialog, setOpenAllocDialog] = useState(false);

    // Form states
    const [hostelName, setHostelName] = useState('');
    const [wardenName, setWardenName] = useState('');
    const [roomNumber, setRoomNumber] = useState('');
    const [capacity, setCapacity] = useState(2);
    const [selectedHostel, setSelectedHostel] = useState('');
    
    // Allocation state
    const [allocStudent, setAllocStudent] = useState('');
    const [allocRoom, setAllocRoom] = useState('');

    useEffect(() => {
        fetchHostels();
        fetchStudents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser._id]);

    const fetchHostels = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/HostelList/${currentUser._id}`);
            if (res.data && res.data.length > 0) {
                setHostels(res.data);
            } else {
                setHostels([]);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchRooms = async (hostelId) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/RoomList/${hostelId}`);
            if (res.data && res.data.length > 0) {
                setRooms(res.data);
            } else {
                setRooms([]);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const fetchStudents = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/Students/${currentUser._id}`);
            if (res.data && res.data.length > 0) {
                setStudents(res.data);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateHostel = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/HostelCreate`, {
                hostelName,
                wardenName,
                school: currentUser._id
            });
            setOpenHostelDialog(false);
            fetchHostels();
        } catch (err) {
            console.error(err);
        }
    };

    const handleCreateRoom = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/RoomCreate`, {
                roomNumber,
                capacity,
                hostel: selectedHostel
            });
            setOpenRoomDialog(false);
            if (selectedHostel) fetchRooms(selectedHostel);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAllocate = async () => {
        try {
            await axios.put(`${process.env.REACT_APP_BASE_URL}/AllocateStudentRoom`, {
                roomId: allocRoom,
                studentId: allocStudent
            });
            setOpenAllocDialog(false);
            if (selectedHostel) fetchRooms(selectedHostel);
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || "Error allocating");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Hostel Management</Typography>
            
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={4}>
                    <Button variant="contained" fullWidth onClick={() => setOpenHostelDialog(true)}>Add New Hostel</Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Button variant="contained" color="secondary" fullWidth onClick={() => setOpenRoomDialog(true)}>Add New Room</Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Button variant="contained" color="success" fullWidth onClick={() => setOpenAllocDialog(true)}>Allocate Room</Button>
                </Grid>
            </Grid>

            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Hostel to View Rooms</InputLabel>
                <Select
                    value={selectedHostel}
                    onChange={(e) => {
                        setSelectedHostel(e.target.value);
                        fetchRooms(e.target.value);
                    }}
                >
                    {hostels.map(h => (
                        <MenuItem key={h._id} value={h._id}>{h.hostelName}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {selectedHostel && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Room Number</TableCell>
                                <TableCell>Capacity</TableCell>
                                <TableCell>Occupants</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rooms.length > 0 ? rooms.map(room => (
                                <TableRow key={room._id}>
                                    <TableCell>{room.roomNumber}</TableCell>
                                    <TableCell>{room.capacity}</TableCell>
                                    <TableCell>
                                        {room.currentOccupants.map(o => o.name).join(", ") || "None"}
                                    </TableCell>
                                    <TableCell>
                                        {room.currentOccupants.length >= room.capacity ? "Full" : "Available"}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No rooms added yet.</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialogs */}
            <Dialog open={openHostelDialog} onClose={() => setOpenHostelDialog(false)}>
                <DialogTitle>Add New Hostel</DialogTitle>
                <DialogContent>
                    <TextField autoFocus margin="dense" label="Hostel Name" fullWidth value={hostelName} onChange={e => setHostelName(e.target.value)} />
                    <TextField margin="dense" label="Warden Name" fullWidth value={wardenName} onChange={e => setWardenName(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenHostelDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateHostel} variant="contained">Add Hostel</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openRoomDialog} onClose={() => setOpenRoomDialog(false)}>
                <DialogTitle>Add New Room</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Select Hostel</InputLabel>
                        <Select value={selectedHostel} onChange={e => setSelectedHostel(e.target.value)}>
                            {hostels.map(h => <MenuItem key={h._id} value={h._id}>{h.hostelName}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <TextField margin="dense" label="Room Number" fullWidth value={roomNumber} onChange={e => setRoomNumber(e.target.value)} />
                    <TextField margin="dense" label="Capacity (e.g. 2)" type="number" fullWidth value={capacity} onChange={e => setCapacity(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenRoomDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateRoom} variant="contained">Add Room</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={openAllocDialog} onClose={() => setOpenAllocDialog(false)}>
                <DialogTitle>Allocate Student to Room</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Select Student</InputLabel>
                        <Select value={allocStudent} onChange={e => setAllocStudent(e.target.value)}>
                            {students.map(s => <MenuItem key={s._id} value={s._id}>{s.name} ({s.rollNum})</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Select Room</InputLabel>
                        <Select value={allocRoom} onChange={e => setAllocRoom(e.target.value)}>
                            {rooms.filter(r => r.currentOccupants.length < r.capacity).map(r => <MenuItem key={r._id} value={r._id}>{r.roomNumber}</MenuItem>)}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenAllocDialog(false)}>Cancel</Button>
                    <Button onClick={handleAllocate} variant="contained" color="success">Allocate</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default AdminHostelManagement;
